import React, {useMemo, useCallback} from 'react';
import {Box, IconButton, InputAdornment, Typography} from '@mui/material';
import './AdvancedSearch.scss';
import {ReactComponent as CloseIcon} from '../../../../../assets/images/close-base-icon.svg';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import ArrMultiSelectDropdown from '../../../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {ISelectItem} from '../../../../../shared/types';
import {ReactComponent as ClearIcon} from '../../../../../assets/images/filter-clear-icon.svg';

import {
  useGetIndicesList,
  useGetOwnersList,
} from '../../../../../hooks/ai/investigate/useInvestigate';
import {
  IAIInvestigateIndicesListItem,
  IAIInvestigateIndicesPayload,
  IAIInvestigateOwnersListResponse,
} from '../../../../../api/types/aiInvestigate.type';
import {
  TranscriptSortColumn,
  TranscriptSortDirection,
} from '../../AiInvestigateUtils';

const TRANSCRIPT_NAME_MAX_LENGTH = 35;
const TRANSCRIPT_DESC_MAX_LENGTH = 100;

export interface AdvancedSearchFilters {
  transcriptName: string;
  transcriptOwner: ISelectItem[];
  indexName: ISelectItem[];
  description: string;
}

interface AdvancedSearchProps {
  filters: AdvancedSearchFilters;
  onChange: (filters: AdvancedSearchFilters) => void;
  onSearch?: (filters: AdvancedSearchFilters) => void;
  onCancel?: () => void;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  filters,
  onChange,
  onSearch,
  onCancel,
}) => {
  const enAdvancedSearch = 'ai.investigate.advancedSearch';
  const {data: ownersList} = useGetOwnersList();
  const indicesPayload: IAIInvestigateIndicesPayload = {
    sortingColumn: TranscriptSortColumn.CreatedOn,
    direction: TranscriptSortDirection.DESC,
    columnFilter: [],
    displayLength: -1,
    pageNumber: 0,
  };
  const {data: getIndicesList} = useGetIndicesList(indicesPayload);

  const ownerOptions = useMemo(() => {
    return (ownersList ?? []).map((item: IAIInvestigateOwnersListResponse) => ({
      id: item.userId,
      name: item.ownerName,
    }));
  }, [ownersList]);

  const indexOptions = useMemo(() => {
    return (getIndicesList?.indices ?? []).map(
      (item: Partial<IAIInvestigateIndicesListItem>) => ({
        id: item.indexId,
        name: item.indexName,
      })
    );
  }, [getIndicesList]);

  const isFormFilled = useMemo(() => {
    return (
      filters.transcriptName.trim().length > 0 ||
      filters.transcriptOwner.length > 0 ||
      filters.indexName.length > 0 ||
      filters.description.trim().length > 0
    );
  }, [filters]);

  const handleTranscriptNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.slice(0, TRANSCRIPT_NAME_MAX_LENGTH);
      onChange({
        ...filters,
        transcriptName: value,
      });
    },
    [filters, onChange]
  );

  const handleTranscriptDescChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.slice(0, TRANSCRIPT_DESC_MAX_LENGTH);
      onChange({
        ...filters,
        description: value,
      });
    },
    [filters, onChange]
  );

  const handleOwnerChange = useCallback(
    (selected: ISelectItem[]) => {
      onChange({
        ...filters,
        transcriptOwner: selected,
      });
    },
    [filters, onChange]
  );

  const handleIndexChange = useCallback(
    (selected: ISelectItem[]) => {
      onChange({
        ...filters,
        indexName: selected,
      });
    },
    [filters, onChange]
  );

  const handleReset = useCallback(() => {
    onChange({
      transcriptName: '',
      transcriptOwner: [],
      indexName: [],
      description: '',
    });
  }, [onChange]);

  const handleSearch = useCallback(() => {
    if (!isFormFilled) return;
    onSearch?.(filters);
  }, [filters, isFormFilled, onSearch]);

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const handleClearTranscriptName = useCallback(() => {
    onChange({
      ...filters,
      transcriptName: '',
    });
  }, [filters, onChange]);

  const handleClearTranscriptDescription = useCallback(() => {
    onChange({
      ...filters,
      description: '',
    });
  }, [filters, onChange]);

  return (
    <Box className="ai-transcript-advanced-search">
      <Box className="advanced-search__header">
        <Typography
          className="typo-text label3_size advanced-search-title-text"
          data-testid="advanced-search-title-text"
        >
          {t(`${enAdvancedSearch}.advancedSearch`)}
        </Typography>
        <IconButton
          onClick={handleCancel}
          className="advanced-search-close-button"
          aria-label={t('common.close')}
          data-testid="advanced-search-close"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className="advanced-search__content">
        <Box className="advanced-search__field">
          <Typography
            className="typo-text label4_size advanced-search__label"
            data-testid="advanced-search-tsname-label"
          >
            {t(`${enAdvancedSearch}.transcriptName`)}
          </Typography>
          <ArrTextField
            fullWidth
            dataTestId="as-transcript-name"
            state="enabled"
            placeholder={t(`${enAdvancedSearch}.search`)}
            sizeVariant="small"
            value={filters.transcriptName}
            onChange={handleTranscriptNameChange}
            aria-label={t(`${enAdvancedSearch}.transcriptName`)}
            slotProps={{
              inputLabel: {shrink: false},
              input: {
                endAdornment: filters.transcriptName ? (
                  <InputAdornment position="end">
                    <IconButton
                      data-testid="transcript-name-clear"
                      onClick={handleClearTranscriptName}
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      aria-label={t('common.clear')}
                    >
                      <ClearIcon className="clear-icon" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        </Box>

        <Box className="advanced-search__field">
          <Typography
            className="typo-text label4_size advanced-search__label"
            data-testid="advanced-search-tsdesc-label"
          >
            {t(`${enAdvancedSearch}.description`)}
          </Typography>
          <ArrTextField
            fullWidth
            dataTestId="as-description"
            state="enabled"
            placeholder={t(`${enAdvancedSearch}.search`)}
            sizeVariant="small"
            value={filters.description}
            onChange={handleTranscriptDescChange}
            aria-label={t(`${enAdvancedSearch}.description`)}
            slotProps={{
              inputLabel: {shrink: false},
              input: {
                endAdornment: filters.description ? (
                  <InputAdornment position="end">
                    <IconButton
                      data-testid="transcript-description-clear"
                      onClick={handleClearTranscriptDescription}
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      aria-label={t('common.clear')}
                    >
                      <ClearIcon className="clear-icon" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        </Box>

        <Box className="advanced-search__field">
          <Typography
            className="typo-text label4_size advanced-search__label"
            data-testid="advanced-search-tsowner-label"
          >
            {t(`${enAdvancedSearch}.transcriptOwner`)}
          </Typography>
          <ArrMultiSelectDropdown
            dropdownLabel={t('common.select')}
            dropdownItems={ownerOptions}
            selectedValues={filters.transcriptOwner}
            onSelectionChange={handleOwnerChange}
            multiple
            searchable
            withControls
            sizeVariant="small"
            isShowTooltip={false}
            onClearAll={() =>
              onChange({
                ...filters,
                transcriptOwner: [],
              })
            }
            onApply={() => {}}
            dataTestId="msd-as-transcript-owner"
            aria-label={t(`${enAdvancedSearch}.transcriptOwner`)}
          />
        </Box>

        <Box className="advanced-search__field">
          <Typography
            className="typo-text label4_size advanced-search__label"
            data-testid="advanced-search-tsindex-label"
          >
            {t(`${enAdvancedSearch}.indexName`)}
          </Typography>
          <ArrMultiSelectDropdown
            dropdownLabel={t('common.select')}
            dropdownItems={indexOptions}
            selectedValues={filters.indexName}
            onSelectionChange={handleIndexChange}
            multiple
            searchable
            withControls
            sizeVariant="small"
            isShowTooltip={false}
            onClearAll={() =>
              onChange({
                ...filters,
                indexName: [],
              })
            }
            onApply={() => {}}
            dataTestId="msd-as-index-name"
            aria-label={t(`${enAdvancedSearch}.indexName`)}
          />
        </Box>
      </Box>
      <Box className="advanced-search__footer">
        <ArrButton
          variant="text"
          size="small"
          onClick={handleReset}
          aria-label={t('common.reset')}
          dataTestId="as-reset-button"
          disabled={!isFormFilled}
        >
          {t('common.reset')}
        </ArrButton>
        <Box>
          <ArrButton
            variant="text"
            size="small"
            aria-label={t('common.cancel')}
            dataTestId="as-cancel-button"
            disabled={false}
            onClick={handleCancel}
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="small"
            type="submit"
            aria-label={t(`${enAdvancedSearch}.search`)}
            dataTestId="as-search-button"
            disabled={!isFormFilled}
            onClick={handleSearch}
          >
            {t(`${enAdvancedSearch}.search`)}
          </ArrButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AdvancedSearch;
